import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FaCrosshairs, FaSearch } from "react-icons/fa";
import { MdLocationCity } from "react-icons/md";
import { createTimeoutSignal } from "../../api";
import { trackEvent } from "../../utils/analytics";
import {
  extractCityLabelsFromNominatim,
  matchCityFromGeocodeLabels,
} from "../../utils/geoCityMatch";
import "./CitySelectModal.css";

/** Curated “popular” order (BookMyShow-style). Picks first matching city from API list. */
function pickPopularCities(cities) {
  if (!Array.isArray(cities) || cities.length === 0) return [];
  const used = new Set();
  const take = (pred) => {
    const hit = cities.find((c) => c?.slug && pred(c) && !used.has(c.slug));
    if (hit) {
      used.add(hit.slug);
      return hit;
    }
    return null;
  };
  const slots = [
    (c) => /^mumbai$/i.test(String(c.slug)) && !/navi/i.test(String(c.slug)),
    (c) => /^(new-?delhi|delhi)$/i.test(String(c.slug)),
    (c) => /bengaluru|bangalore/i.test(String(c.slug)) || /bengaluru|bangalore/i.test(String(c.name || "")),
    (c) => /hyderabad/i.test(String(c.slug)),
    (c) => /chandigarh/i.test(String(c.slug)),
    (c) => /ahmedabad/i.test(String(c.slug)),
    (c) => /^pune$/i.test(String(c.slug)),
    (c) => /chennai/i.test(String(c.slug)),
    (c) => /kolkata/i.test(String(c.slug)),
    (c) => /kochi|cochin/i.test(String(c.slug)),
  ];
  const out = [];
  for (const pred of slots) {
    const item = take(pred);
    if (item) out.push(item);
  }
  return out;
}

/**
 * Prefer same-origin /nominatim (CRA proxy + Netlify redirect) so User-Agent policy and CORS work.
 * Falls back to direct OSM if needed (may fail in browser due to CORS).
 */
async function fetchNominatimReverseJson(lat, lon) {
  const qs = `lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&format=json&addressdetails=1`;
  const headers = { Accept: "application/json", "Accept-Language": "en" };
  const signal = createTimeoutSignal(18000);

  const pathPrefix = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  try {
    const localRes = await fetch(`${pathPrefix}/nominatim/reverse?${qs}`, {
      headers,
      signal,
    });
    if (localRes.ok) return localRes.json();
  } catch {
    /* no dev proxy or timeout */
  }

  const res = await fetch(`https://nominatim.openstreetmap.org/reverse?${qs}`, {
    headers,
    signal: createTimeoutSignal(18000),
  });
  if (!res.ok) throw new Error("Geocode failed");
  return res.json();
}

/**
 * BookMyShow-inspired city picker: search, geolocation, popular row, view all.
 */
const CitySelectModal = ({
  show,
  onClose,
  cities = [],
  loading = false,
  selectedSlug = "",
  onSelectCity,
  /** When false, overlay / Escape do not close (first-visit flow). */
  allowDismiss = true,
  /** Show a visible heading (e.g. first visit). */
  showTitle = false,
  /** One-line note under “Detect my location”. */
  showPrivacyHint = false,
  /** Optional secondary action (e.g. first-visit default city). */
  continueWithDefaultLabel = "",
  onContinueWithDefault,
}) => {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [geoState, setGeoState] = useState("idle");
  const [geoMessage, setGeoMessage] = useState("");

  const popular = useMemo(() => {
    const p = pickPopularCities(cities);
    if (p.length) return p;
    return cities.slice(0, Math.min(10, cities.length));
  }, [cities]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => {
      const name = (c?.name ?? "").toString().toLowerCase();
      const slug = (c?.slug ?? "").toString().toLowerCase();
      return name.includes(q) || slug.includes(q);
    });
  }, [cities, search]);

  const selectedNorm = (selectedSlug || "").toString().toLowerCase();

  const handleSelect = useCallback(
    (city) => {
      if (!city) return;
      onSelectCity?.(city);
      onClose?.();
    },
    [onSelectCity, onClose]
  );

  const detectLocation = useCallback(() => {
    if (!navigator.geolocation) {
      trackEvent("city_detect_error", { reason: "not_supported" });
      setGeoMessage("Location is not supported in this browser.");
      setGeoState("error");
      return;
    }
    if (!cities.length) {
      setGeoMessage("Cities are still loading. Please wait a moment and try again.");
      setGeoState("error");
      return;
    }

    setGeoState("loading");
    setGeoMessage("");
    trackEvent("city_detect_started", {});

    const onSuccess = async (pos) => {
      try {
        const data = await fetchNominatimReverseJson(pos.coords.latitude, pos.coords.longitude);
        const labels = extractCityLabelsFromNominatim(data);
        const match = matchCityFromGeocodeLabels(labels, cities);
        if (match) {
          trackEvent("city_detect_matched", { city_slug: match.slug || "" });
          handleSelect(match);
          setGeoState("idle");
          return;
        }
        const hint = labels[0];
        trackEvent("city_detect_unmatched", { hint: hint || "" });
        setGeoMessage(
          hint
            ? `We detected “${hint}” but couldn’t match it to a city in our list. Try searching above.`
            : "Couldn’t read a city name from your location. Try searching above."
        );
        setGeoState("error");
      } catch {
        trackEvent("city_detect_error", { reason: "network_or_geocode" });
        setGeoMessage(
          "Couldn’t reach the location service. Check your connection or try again in a moment."
        );
        setGeoState("error");
      }
    };

    const onErr = (err) => {
      const code = err?.code;
      trackEvent("city_detect_error", { reason: `geo_${code ?? "unknown"}` });
      if (code === 1) {
        setGeoMessage("Location access was denied. Allow location in your browser settings, or pick a city from the list.");
      } else if (code === 2) {
        setGeoMessage("Your position couldn’t be determined. Try again or select a city manually.");
      } else if (code === 3) {
        setGeoMessage("Location request timed out. Try again or select a city manually.");
      } else {
        setGeoMessage("Couldn’t get your location. Try searching for a city above.");
      }
      setGeoState("error");
    };

    navigator.geolocation.getCurrentPosition(onSuccess, onErr, {
      enableHighAccuracy: true,
      timeout: 22000,
      maximumAge: 120000,
    });
  }, [cities, handleSelect]);

  useEffect(() => {
    if (!show) return;
    setSearch("");
    setShowAll(false);
    setGeoState("idle");
    setGeoMessage("");
  }, [show]);

  useEffect(() => {
    if (!show || !allowDismiss) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, onClose, allowDismiss]);

  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  if (!show) return null;

  const modal = (
    <div
      className={`city-select-modal-overlay${allowDismiss ? "" : " city-select-modal-overlay--blocking"}`}
      onClick={allowDismiss ? onClose : undefined}
      role="presentation"
    >
      <div
        className="city-select-modal proppins-modal-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="city-select-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="city-select-modal-title"
          className={showTitle ? "city-select-modal__heading" : "city-select-modal__title visually-hidden"}
        >
          Select your city
        </h2>
        {showTitle && (
          <p className="city-select-modal__subtitle">Choose a city to see listings in your area.</p>
        )}

        {continueWithDefaultLabel && typeof onContinueWithDefault === "function" && (
          <button
            type="button"
            className="city-select-modal__continue-default"
            onClick={onContinueWithDefault}
          >
            {continueWithDefaultLabel}
          </button>
        )}

        <div className="city-select-modal__search-wrap">
          <FaSearch className="city-select-modal__search-icon" aria-hidden />
          <input
            type="search"
            className="city-select-modal__search-input"
            placeholder="Search for your city"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
            autoFocus
          />
        </div>

        <button
          type="button"
          className="city-select-modal__detect"
          onClick={detectLocation}
          disabled={geoState === "loading" || loading}
        >
          <FaCrosshairs className="city-select-modal__detect-icon" aria-hidden />
          {geoState === "loading" ? "Detecting…" : "Detect my location"}
        </button>
        {showPrivacyHint && (
          <p className="city-select-modal__privacy-hint">
            We use your approximate location once to suggest a city. It is not stored on our servers.
          </p>
        )}
        {geoMessage && <p className="city-select-modal__geo-msg">{geoMessage}</p>}

        {!showAll && popular.length > 0 && !search.trim() && (
          <div className="city-select-modal__popular">
            <p className="city-select-modal__section-label">Popular Cities</p>
            <div className="city-select-modal__popular-row">
              {popular.map((city) => {
                const slug = (city.slug || "").toLowerCase();
                const active = selectedNorm && slug === selectedNorm;
                return (
                  <button
                    key={city.slug}
                    type="button"
                    className={`city-select-modal__city-tile ${active ? "city-select-modal__city-tile--active" : ""}`}
                    onClick={() => handleSelect(city)}
                  >
                    <span className="city-select-modal__city-icon-wrap" aria-hidden>
                      <MdLocationCity className="city-select-modal__city-icon" />
                    </span>
                    <span className="city-select-modal__city-name">{city.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {(showAll || search.trim()) && (
          <div className="city-select-modal__list-wrap">
            <p className="city-select-modal__section-label">
              {search.trim() ? "Results" : "All cities"}
            </p>
            <ul className="city-select-modal__list">
              {loading ? (
                <li className="city-select-modal__list-empty">Loading cities…</li>
              ) : filtered.length === 0 ? (
                <li className="city-select-modal__list-empty">
                  {search.trim() ? "No cities match your search." : "No cities available."}
                </li>
              ) : (
                filtered.map((city) => {
                  const slug = (city.slug || "").toLowerCase();
                  const active = selectedNorm && slug === selectedNorm;
                  return (
                    <li key={city.slug}>
                      <button
                        type="button"
                        className={`city-select-modal__list-item ${active ? "city-select-modal__list-item--active" : ""}`}
                        onClick={() => handleSelect(city)}
                      >
                        {city.name}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}

        {!search.trim() && (
          <button
            type="button"
            className="city-select-modal__view-all"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Show popular cities" : "View all cities"}
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default CitySelectModal;
